import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, ArrowRight, CheckCircle2, Loader2, Linkedin, Github, Facebook, Twitter, Instagram } from 'lucide-react';
import { motion } from 'motion/react';
import { newsletterService } from '../services/newsletterService';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      setSubmitting(true);
      setError(null);
      const response = await newsletterService.subscribe(email, 'footer');
      if (response.success) {
        setSubscribed(true);
        setEmail('');
      } else {
        setError(response.message || 'Subscription failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#050505] text-slate-300 pt-20 pb-10 border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-brand-900/10 via-[#050505] to-[#050505] opacity-100 pointer-events-none"></div>
      <div className="w-full px-6 sm:px-10 lg:px-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand */}
          <div className="space-y-6 lg:col-span-1">
            <div className="flex items-center gap-2">
              <div className="relative px-3 py-1.5 transition-transform hover:scale-105 inline-block">
                <img
                  src="https://www.princeitsolution.com/assets/images/white-logo.png?v=1.2"
                  alt="Prince IT"
                  className="relative h-10 w-auto"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                />
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs font-light">
              Design, Build and Grow with a Trusted IT Partner. We deliver tailored technology solutions to empower your business globally.
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-display font-semibold mb-6">Services</h3>
            <ul className="space-y-4">
              <li><Link to="/services/web-development" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Web Development</Link></li>
              <li><Link to="/services/mobile-app" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Mobile App Development</Link></li>
              <li><Link to="/services/uiux-design" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">UI/UX Design</Link></li>
              <li><Link to="/services/digital-marketing" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Digital Marketing</Link></li>
              <li><Link to="/services/cloud-devops" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Cloud & DevOps</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-display font-semibold mb-6">Company</h3>
            <ul className="space-y-4">
              <li><Link to="/about-us" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">About Us</Link></li>
              <li><Link to="/portfolio" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Portfolio</Link></li>
              <li><Link to="/technology" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Technology</Link></li>
              <li><Link to="/blog" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Blog & Insights</Link></li>
              <li><Link to="/contact-us" className="text-sm text-slate-400 hover:text-brand-400 transition-all duration-300 hover:translate-x-1 inline-block">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-display font-semibold mb-6">Get in Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-400 group">
                <MapPin className="w-5 h-5 text-brand-400 shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Prince+IT+Solution+Silver+Trade+Center+Mota+Varachha+Surat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-light hover:text-brand-400 transition-colors"
                >
                  Prince IT Solution, 504, Silver Trade Center, Beside Oxygen Park, Mota Varachha, Surat Gujarat 394105
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-400 group">
                <Phone className="w-5 h-5 text-brand-400 shrink-0 group-hover:scale-110 transition-transform" />
                <a
                  href="tel:+919023796107"
                  className="text-sm font-light hover:text-brand-400 transition-colors"
                >
                  +91 90237 96107
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-400 group">
                <Mail className="w-5 h-5 text-brand-400 shrink-0 group-hover:scale-110 transition-transform" />
                <a
                  href="mailto:sales@princeitsolution.com"
                  className="text-sm font-light hover:text-brand-400 transition-colors"
                >
                  sales@princeitsolution.com
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-1">
            <h3 className="text-white font-display font-semibold mb-6">Stay Updated</h3>
            <p className="text-sm text-slate-400 mb-6 font-light">Get the latest insights delivered to your inbox.</p>

            {subscribed ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-brand-400"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">Successfully subscribed!</span>
              </motion.div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    required
                    disabled={submitting}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-sm text-white focus:outline-none focus:border-brand-500/50 transition-colors disabled:opacity-50"
                  />
                </div>
                {error && (
                  <p className="text-red-400 text-xs px-1">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-brand-500 text-[#050505] py-3 rounded-xl text-sm font-bold hover:bg-brand-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      Subscribe
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500 font-light">
            © {new Date().getFullYear()} Prince IT Solution. All rights reserved.
          </p>
          <div className="flex gap-4">
            <a href="https://www.facebook.com/PrinceITSolutionIndia" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-brand-500 hover:text-[#050505] hover:border-brand-500 transition-all duration-300">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://x.com/Prince_IT_Sol" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-brand-500 hover:text-[#050505] hover:border-brand-500 transition-all duration-300">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="https://www.instagram.com/princeit_solution" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-brand-500 hover:text-[#050505] hover:border-brand-500 transition-all duration-300">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://www.linkedin.com/company/princeitsolution" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-brand-500 hover:text-[#050505] hover:border-brand-500 transition-all duration-300">
              <Linkedin className="w-5 h-5" />
            </a>
            <a href="https://github.com/Prince-IT-Solution" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-brand-500 hover:text-[#050505] hover:border-brand-500 transition-all duration-300">
              <Github className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
